// Cuts the unit square into irregular triangles that tile it exactly.
// Seeded, so every client breaks the same image the same way.

export type Point = readonly [number, number]

export type Shard = {
	id: number
	/** Triangle corners in 0..1 UV space */
	points: readonly [Point, Point, Point]
	centroid: Point
	/** Grid cell, handy for picking "edge" shards to chip off first */
	col: number
	row: number
}

export function mulberry32(seed: number) {
	let a = seed >>> 0
	return () => {
		a = (a + 0x6d2b79f5) >>> 0
		let t = a
		t = Math.imul(t ^ (t >>> 15), t | 1)
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
}

export function buildShards({
	cols,
	rows,
	jitter = 0.35,
	seed = 1,
}: {
	cols: number
	rows: number
	/** How far interior grid points may wander, as a fraction of a cell */
	jitter?: number
	seed?: number
}): Shard[] {
	const rand = mulberry32(seed)
	const cellW = 1 / cols
	const cellH = 1 / rows

	// Grid points; only interior ones move so the outline stays a rectangle.
	const grid: Point[][] = []
	for (let r = 0; r <= rows; r++) {
		const line: Point[] = []
		for (let c = 0; c <= cols; c++) {
			const interiorX = c > 0 && c < cols
			const interiorY = r > 0 && r < rows
			const dx = interiorX ? (rand() - 0.5) * 2 * jitter * cellW : 0
			const dy = interiorY ? (rand() - 0.5) * 2 * jitter * cellH : 0
			line.push([c * cellW + dx, r * cellH + dy])
		}
		grid.push(line)
	}

	const shards: Shard[] = []
	for (let r = 0; r < rows; r++) {
		for (let c = 0; c < cols; c++) {
			const tl = grid[r]![c]!
			const tr = grid[r]![c + 1]!
			const br = grid[r + 1]![c + 1]!
			const bl = grid[r + 1]![c]!
			const tris: Array<[Point, Point, Point]> =
				rand() < 0.5
					? [
							[tl, tr, br],
							[tl, br, bl],
						]
					: [
							[tl, tr, bl],
							[tr, br, bl],
						]
			for (const points of tris) {
				shards.push({
					id: shards.length,
					points,
					centroid: [
						(points[0][0] + points[1][0] + points[2][0]) / 3,
						(points[0][1] + points[1][1] + points[2][1]) / 3,
					],
					col: c,
					row: r,
				})
			}
		}
	}
	return shards
}

export function triangleArea([a, b, c]: readonly [Point, Point, Point]) {
	return Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1])) / 2
}
